import React from 'react';
import { FileArchiveIcon } from 'lucide-react';
import { FILE_TYPE_META } from '../../utils/fileTypes';
import { formatSize } from '../../utils/format';
import type { FsFile } from '../../types/files';
interface PreviewContentProps {
  file: FsFile;
  page: number;
  zoom: number;
}

/** Number of pages/sheets a given document renders in the viewer. */
export function pageCountFor(file: FsFile): number {
  if (file.type === 'img' || file.type === 'zip') return 1;
  if (file.type === 'ppt') return 5;
  if (file.type === 'txt' || file.type === 'html' || file.type === 'csv') return 1;
  return Math.max(1, Math.min(8, Math.round(file.sizeKb / 220) || 1));
}
export function PreviewContent({
  file,
  page,
  zoom
}: PreviewContentProps) {
  if (file.type === 'img') {
    return <div className="flex min-h-full items-center justify-center p-6" data-id="element-210" data-mp="191kgodc41h7c">
        {file.thumbnailUrl ? <img src={file.thumbnailUrl} alt={file.name} style={{
        width: `${zoom}%`
      }} className="max-w-none rounded shadow-2xl" data-id="element-211" data-mp="1dskfjo4si9ch" /> : <PlaceholderSheet file={file} data-id="element-212" data-mp="3jkg7zbjgbhz2" />}
      </div>;
  }
  if (file.type === 'zip') {
    return <Sheet zoom={zoom} data-id="element-213" data-mp="24hwjm1450kq">
        <div className="flex items-center gap-2 border-b border-slate-200 pb-3" data-id="element-214" data-mp="2516afgm0ino">
          <FileArchiveIcon className="h-5 w-5 text-amber-700" aria-hidden="true" data-id="element-215" data-mp="3thc92w70t1tl" />
          <h3 className="text-sm font-semibold text-slate-900" data-id="element-216" data-mp="b3d2cx08w8ni">
            Archive contents
          </h3>
        </div>
        <ul className="mt-3 divide-y divide-slate-100 text-sm" data-id="element-217" data-mp="71rll0klba81">
          {[['2019 Loan Application.pdf', 1240], ['2019 Closing Disclosure.pdf', 820], ['2019 Appraisal.pdf', 2980], ['Payoff Statement.pdf', 180], ['Correspondence/', 0]].map(([name, size]) => <li key={name as string} className="flex items-center justify-between py-2" data-id="element-218" data-mp="2mvicts7shi1d">
              <span className="truncate text-slate-800" data-id="element-219" data-mp="lrzbsq3fjjr7">{name}</span>
              <span className="tabular-nums text-xs text-slate-500" data-id="element-220" data-mp="ltsmqhl2sq50">
                {size ? formatSize(size as number) : '—'}
              </span>
            </li>)}
        </ul>
        <p className="mt-4 text-xs text-slate-500" data-id="element-221" data-mp="uu8o9li9dn78">
          Archives are not previewed in full. Download to inspect every entry.
        </p>
      </Sheet>;
  }
  if (file.type === 'xls' || file.type === 'csv') {
    const columns = ['A', 'B', 'C', 'D', 'E'];
    const headers = ['Account', 'Institution', 'Period', 'Balance', 'Verified'];
    const rows = [['••••4821', 'Meridian Bank', 'Jun 2026', '$42,180.22', 'Yes'], ['••••4821', 'Meridian Bank', 'Jul 2026', '$38,904.67', 'Yes'], ['••••4821', 'Meridian Bank', 'Aug 2026', '$46,512.09', 'Yes'], ['••••9930', 'Harbor Credit Union', 'Jun 2026', '$18,220.00', 'Yes'], ['••••9930', 'Harbor Credit Union', 'Jul 2026', '$18,455.31', 'Yes'], ['••••9930', 'Harbor Credit Union', 'Aug 2026', '$21,004.88', 'Pending'], ['Retirement', 'Vanguard', 'Aug 2026', '$164,730.15', 'Yes']];
    return <Sheet zoom={zoom} wide data-id="element-222" data-mp="1xmtbd4tyjpfu">
        <table className="w-full border-collapse text-sm" data-id="element-223" data-mp="1sldm10kqywrn">
          <thead data-id="element-224" data-mp="1slx3yhaedl5o">
            <tr data-id="element-225" data-mp="1sngl0a3vo08i">
              <th className="w-10 border border-slate-200 bg-slate-100 px-2 py-1 text-xs font-medium text-slate-500" data-id="element-226" data-mp="1okbmsjqtykpo" />
              {columns.map(column => <th key={column} className="border border-slate-200 bg-slate-100 px-2 py-1 text-xs font-medium text-slate-500" data-id="element-227" data-mp="2lcf7tkiy3qk5">
                  {column}
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-228" data-mp="27i4mld65qeiu">
            <tr data-id="element-229" data-mp="27inzgfgyacq0">
              <td className="border border-slate-200 bg-slate-100 px-2 py-1 text-center text-xs text-slate-500" data-id="element-230" data-mp="27h3b57l8bq0l">
                1
              </td>
              {headers.map(header => <td key={header} className="border border-slate-200 bg-slate-50 px-2 py-1 font-semibold text-slate-800" data-id="element-231" data-mp="2z897dcxtx3kl">
                  {header}
                </td>)}
            </tr>
            {rows.map((row, index) => <tr key={index} data-id="element-232" data-mp="2atgzj35e1msj" data-mpi={index}>
                <td className="border border-slate-200 bg-slate-100 px-2 py-1 text-center text-xs text-slate-500" data-id="element-233" data-mp="2esg2wjbh1bhv">
                  {index + 2}
                </td>
                {row.map((cell, cellIndex) => <td key={cellIndex} className={['border border-slate-200 px-2 py-1 text-slate-700', cellIndex === 3 ? 'text-right tabular-nums' : ''].join(' ')} data-id="element-234" data-mp="37hww3j6x0wf5" data-mpi={cellIndex}>
                    {cell}
                  </td>)}
              </tr>)}
          </tbody>
        </table>
      </Sheet>;
  }
  if (file.type === 'txt' || file.type === 'html') {
    const lines = file.type === 'html' ? ['<!doctype html>', '<html lang="en">', '  <body style="font-family: sans-serif">', '    <h1>Rate Lock Confirmation</h1>', '    <p>Loan ML-2026-004871 · 30-Year Fixed</p>', '    <p>Locked rate: 5.875% · Expires Sep 30, 2026</p>', '    <p>Lock desk reference: LD-88213</p>', '  </body>', '</html>'] : ['Call with borrower — escrow follow-up', 'Sep 14, 2026 · 11:20 AM · 14 min', '', '- Confirmed homeowners policy effective date of Oct 1.', '- Borrower will send updated binder from insurer today.', '- Escrow account to include $312/mo taxes, $118/mo insurance.', '- Reminded borrower not to open new credit before closing.', '', 'Next step: re-run final credit refresh on Sep 24.'];
    return <Sheet zoom={zoom} data-id="element-235" data-mp="2jaq17rwtdhh2">
        <pre className="whitespace-pre-wrap break-words font-mono text-[13px] leading-6 text-slate-800" data-id="element-236" data-mp="2jbhx73ubph7v">
          {lines.join('\n')}
        </pre>
      </Sheet>;
  }
  if (file.type === 'ppt') {
    return <Sheet zoom={zoom} wide data-id="element-237" data-mp="2t7umzqmqzaxc">
        <div className="aspect-[16/9] w-full rounded bg-[#0b2545] p-8 text-white" data-id="element-238" data-mp="2y8qvkhi1nl93">
          <p className="text-xs uppercase tracking-[0.2em] text-sky-200" data-id="element-239" data-mp="20la0b3h7vn04">
            Slide {page}
          </p>
          <h3 className="mt-3 text-2xl font-semibold" data-id="element-240" data-mp="25k3aon17ixw2">
            {page === 1 ? 'Welcome to your refinance' : 'What happens next'}
          </h3>
          <ul className="mt-5 space-y-2.5 text-sm text-sky-100" data-id="element-241" data-mp="2ahv6vt8vx662">
            {['Your dedicated loan officer and timeline', 'Documents we still need from you', 'How underwriting reviews your file', 'Closing day and funding'].map(item => <li key={item} className="flex items-start gap-2" data-id="element-242" data-mp="1mrr0xf884ubw">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-300" data-id="element-243" data-mp="3xphwfh123ah" />
                {item}
              </li>)}
          </ul>
        </div>
      </Sheet>;
  }
  return <DocumentSheet file={file} page={page} zoom={zoom} data-id="element-244" data-mp="3r2dvb182000a" />;
}
function DocumentSheet({
  file,
  page,
  zoom
}: {
  file: FsFile;
  page: number;
  zoom: number;
}) {
  const meta = FILE_TYPE_META[file.type];
  return <Sheet zoom={zoom} data-id="element-245" data-mp="3h6u24mkvjff8">
      <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-4" data-id="element-246" data-mp="3h73swcdqtqlz">
        <div data-id="element-247" data-mp="3ccpqvr4ksl8c">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0176d3]" data-id="element-248" data-mp="38cyv1906zewy">
            Meridian Home Lending
          </p>
          <h3 className="mt-1 text-lg font-semibold text-slate-900" data-id="element-249" data-mp="33f5o2dr2egvm">
            {file.name}
          </h3>
          <p className="mt-0.5 text-xs text-slate-500" data-id="element-250" data-mp="2ye9lq4md32sb">
            Loan ML-2026-004871 · {meta.label} · Page {page}
          </p>
        </div>
        <div className="h-10 w-10 rounded bg-[#0b2545]" aria-hidden="true" data-id="element-251" data-mp="m4huqr3is1oh" />
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-sm" data-id="element-252" data-mp="m2nalf02odlg">
        {[['Borrower', 'Jordan A. Whitfield'], ['Property', '412 Larkspur Ln, Fairview'], ['Loan amount', '$486,000'], ['Product', '30-Year Fixed Refinance'], ['Prepared by', file.owner], ['Document version', `v${file.version}`]].map(([label, value]) => <div key={label} data-id="element-253" data-mp="2envjryh6ka8">
            <dt className="text-xs font-medium text-slate-500" data-id="element-254" data-mp="77quygpxonyc">{label}</dt>
            <dd className="text-slate-900" data-id="element-255" data-mp="3pl6spop1e32j">{value}</dd>
          </div>)}
      </dl>

      <div className="mt-6 space-y-5" data-id="element-256" data-mp="3pjdijzf7t0ps">
        {[0, 1, 2].map(block => <div key={block} data-id="element-257" data-mp="3ukjes7833642">
            <p className="text-sm font-semibold text-slate-900" data-id="element-258" data-mp="3fn0qt738cwxo">
              {['Section 1 — Borrower certification', 'Section 2 — Verified income and assets', 'Section 3 — Conditions and acknowledgements'][block]}
            </p>
            <div className="mt-2 space-y-2" data-id="element-259" data-mp="2wzrxobnm5y9w">
              {Array.from({
            length: 5
          }).map((_, line) => <div key={line} className="h-2.5 rounded bg-slate-200" style={{
            width: `${[100, 96, 88, 99, 62][line]}%`
          }} data-id="element-260" data-mp="3azj9c5qnr2kq" data-mpi={line} />)}
            </div>
          </div>)}
      </div>

      <div className="mt-8 flex items-end justify-between border-t border-slate-200 pt-5" data-id="element-261" data-mp="3fxue4223imw3">
        <div data-id="element-262" data-mp="3tozh9e4o9xb6">
          <div className="h-8 w-44 rounded bg-slate-100" data-id="element-263" data-mp="3tpipg34it7c0" />
          <p className="mt-1 text-xs text-slate-500" data-id="element-264" data-mp="3tr3kdtuqysae">Borrower signature</p>
        </div>
        <p className="text-xs text-slate-400" data-id="element-265" data-mp="3oqo9d8xxtwvl">
          {file.name}.{file.extension} · page {page}
        </p>
      </div>
    </Sheet>;
}
function PlaceholderSheet({
  file
}: {
  file: FsFile;
}) {
  const meta = FILE_TYPE_META[file.type];
  const Icon = meta.icon;
  return <div className="flex flex-col items-center gap-3 rounded bg-white p-12 text-center shadow-2xl" data-id="element-266" data-mp="19hytu1rovclm">
      <Icon className={`h-10 w-10 ${meta.iconClass}`} aria-hidden="true" data-id="element-267" data-mp="14powi0o8va9b" />
      <p className="text-sm font-semibold text-slate-900" data-id="element-268" data-mp="zqm0t6u5mge0">
        Preview not available
      </p>
      <p className="max-w-xs text-sm text-slate-600" data-id="element-269" data-mp="vqlb2q1luirn">
        Download the file to view it in its native application.
      </p>
    </div>;
}
function Sheet({
  children,
  zoom,
  wide = false
}: {
  children: React.ReactNode;
  zoom: number;
  wide?: boolean;
}) {
  return <div className="flex min-h-full justify-center p-6" data-id="element-270" data-mp="1jf4iz4gqstj7">
      <div style={{
      width: `${zoom}%`,
      maxWidth: wide ? '64rem' : '48rem'
    }} className="h-fit rounded bg-white p-8 shadow-2xl" data-id="element-271" data-mp="1jdjp7h295eme">
        {children}
      </div>
    </div>;
}